import { useState, useCallback } from 'react';
import { useTr } from '../utils/useTr';
import { useApp } from '../contexts/AppContext';
import { formatEventDate } from '../utils/events';
import { payForEvent, refundPayment, isRefundEligible,
  formatCardNumber, formatExpiry, formatCVC, getEventPrice } from '../utils/stripe';

// ── Card brand detection ───────────────────────────────────────────────────
function detectCardBrand(num) {
  const n = (num || '').replace(/\s/g, '');
  if (/^4/.test(n))          return 'visa';
  if (/^5[1-5]/.test(n))     return 'mastercard';
  if (/^3[47]/.test(n))      return 'amex';
  if (/^6(?:011|5)/.test(n)) return 'discover';
  return 'unknown';
}

// ── Refund deadline (48 h before event) ──────────────────────────────────
function getRefundDeadline(event) {
  if (!event?.date) return null;
  const d = new Date(`${event.date}T${event.time || '00:00'}`);
  if (isNaN(d.getTime())) return null;
  d.setHours(d.getHours() - 48);
  return d.toLocaleDateString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
}

// ── Test card hints ────────────────────────────────────────────────────────
const TEST_CARDS = [
  { number: '4242 4242 4242 4242', label: 'Success' },
  { number: '4000 0000 0000 0002', label: 'Declined' },
  { number: '4000 0000 0000 9995', label: 'Funds' },
];

// ── Card brand icon ────────────────────────────────────────────────────────
function CardIcon({ brand }) {
  if (brand === 'visa') return (
    <svg width="28" height="18" viewBox="0 0 28 18" fill="none">
      <rect width="28" height="18" rx="3" fill="#1A1F71"/>
      <text x="14" y="13" textAnchor="middle" fill="white" fontSize="8" fontWeight="700" fontFamily="sans-serif">VISA</text>
    </svg>
  );
  if (brand === 'mastercard') return (
    <svg width="28" height="18" viewBox="0 0 28 18" fill="none">
      <rect width="28" height="18" rx="3" fill="#252525"/>
      <circle cx="10" cy="9" r="6" fill="#EB001B"/>
      <circle cx="18" cy="9" r="6" fill="#F79E1B"/>
      <path d="M14 4.8a6 6 0 0 1 0 8.4A6 6 0 0 1 14 4.8z" fill="#FF5F00"/>
    </svg>
  );
  return (
    <svg width="28" height="18" viewBox="0 0 28 18" fill="none">
      <rect width="28" height="18" rx="3" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5"/>
      <rect x="4" y="7" width="20" height="4" rx="1" fill="rgba(255,255,255,0.2)"/>
    </svg>
  );
}

// ── Form field wrapper ─────────────────────────────────────────────────────
function CardField({ label, children, error }) {
  return (
    <div style={{ marginBottom:'0.7rem' }}>
      <label style={{ fontSize:'0.62rem', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--muted)', display:'block', marginBottom:'0.3rem' }}>{label}</label>
      {children}
      {error && <div style={{ fontSize:'0.65rem', color:'#E07070', marginTop:'0.2rem' }}>{error}</div>}
    </div>
  );
}

// ── Main checkout sheet ────────────────────────────────────────────────────
export default function StripeCheckout({ event, onClose, onSuccess }) {
  const tr = useTr();
  const { currentUser, setRegisteredEvents } = useApp();

  const price     = getEventPrice(event, currentUser);
  const fullPrice = event?.price ?? 0;
  const discount  = Math.max(0, fullPrice - price);
  const isFree    = price === 0;
  const isDev     = process.env.REACT_APP_ENV !== 'production';
  const deadline  = getRefundDeadline(event);

  const [step,     setStep]     = useState('form'); // form | processing | success | error
  const [receipt,  setReceipt]  = useState(null);
  const [errors,   setErrors]   = useState({});
  const [errorMsg, setErrorMsg] = useState('');

  // Form fields
  const [name,   setName]   = useState(currentUser?.name || '');
  const [email,  setEmail]  = useState(currentUser?.email || '');
  const [card,   setCard]   = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc,    setCvc]    = useState('');

  const brand = detectCardBrand(card);

  // ── Validation ─────────────────────────────────────────────────────────
  const validate = useCallback(() => {
    const errs = {};
    if (!name.trim()) errs.name = 'Required';
    if (!email.match(/^[^@]+@[^@]+\.[^@]+$/)) errs.email = 'Valid email required';
    if (!isFree) {
      if (card.replace(/\s/g, '').length < 15) errs.card = 'Enter a valid card number';
      if (expiry.length < 5) errs.expiry = 'MM/YY required';
      if (cvc.length < 3)    errs.cvc    = '3-digit CVV required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [name, email, card, expiry, cvc, isFree]);

  // ── Submit ──────────────────────────────────────────────────────────────
  const handlePay = async () => {
    if (!validate()) return;
    setStep('processing');
    setErrorMsg('');

    try {
      let rcpt;
      if (isFree) {
        await new Promise(r => setTimeout(r, 600));
        rcpt = {
          id:        `free_${Date.now()}`,
          eventId:   event.id,
          userId:    currentUser?.id,
          name, email,
          amount:    0,
          paymentId: null,
          date:      new Date().toISOString(),
        };
      } else {
        const { supabase: sb } = await import('../utils/supabase');
        const { data: { session } } = await sb.auth.getSession();
        const token = session?.access_token;

        const { createPaymentIntent } = await import('../utils/api');
        const { clientSecret, paymentIntentId, error: piErr } = await createPaymentIntent({
          amount:      price,
          currency:    'cad',
          eventId:     event.id,
          description: event.title,
        }, token);
        if (piErr) throw new Error(piErr);

        const { getStripe } = await import('../utils/stripe');
        const stripeLib = await getStripe();
        if (!stripeLib) throw new Error('Stripe not loaded');

        const [expMonth, expYear] = expiry.split('/');
        const { paymentIntent, error: confirmErr } = await stripeLib.confirmCardPayment(clientSecret, {
          payment_method: {
            card: {
              number:    card.replace(/\s/g, ''),
              exp_month: parseInt(expMonth),
              exp_year:  parseInt('20' + expYear),
              cvc,
            },
            billing_details: { name, email },
          },
        });
        if (confirmErr)                             throw new Error(confirmErr.message);
        if (paymentIntent?.status !== 'succeeded')  throw new Error('Payment not completed');

        rcpt = {
          paymentId: paymentIntentId,
          eventId:   event.id,
          userId:    currentUser?.id,
          amount:    price,
          currency:  'CAD',
          cardLast4: card.replace(/\s/g, '').slice(-4),
          date:      new Date().toISOString(),
          name, email,
        };
      }

      setRegisteredEvents?.(prev => new Set([...prev, event.id]));
      setReceipt(rcpt);
      setStep('success');
      setTimeout(() => onSuccess?.(rcpt), 200);

    } catch (err) {
      setStep('error');
      setErrorMsg(err.message || 'Payment failed. Please try again.');
    }
  };

  // ── Success ─────────────────────────────────────────────────────────────
  if (step === 'success' && receipt) {
    return (
      <div style={{ padding:'1.5rem', textAlign:'center' }}>
        <div style={{ width:56, height:56, borderRadius:'50%', background:'rgba(78,201,148,0.15)', border:'1px solid rgba(78,201,148,0.4)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1rem' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4EC994" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:400, color:'var(--white)', marginBottom:'0.3rem' }}>
          {tr('payment.ticket_ready') || 'You\'re registered!'}
        </div>
        <div style={{ fontSize:'0.75rem', color:'var(--muted)', marginBottom:'1.2rem' }}>
          {tr('payment.email_conf') || 'Confirmation sent to'} <strong style={{ color:'var(--white)' }}>{receipt.email}</strong>
        </div>
        <div style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'0.9rem', textAlign:'left', marginBottom:'1.2rem' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.05rem', color:'var(--white)', marginBottom:'0.3rem' }}>{event.title}</div>
          <div style={{ fontSize:'0.72rem', color:'var(--muted)' }}>{formatEventDate(event.date)} · {event.time} — {event.location}</div>
          {price > 0 && (
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:'0.6rem', paddingTop:'0.6rem', borderTop:'1px solid var(--border)' }}>
              <span style={{ fontSize:'0.7rem', color:'var(--muted)' }}>{tr('payment.total') || 'Total'}</span>
              <span style={{ fontSize:'0.82rem', fontWeight:700, color:'var(--gold)' }}>${price.toFixed(2)} CAD</span>
            </div>
          )}
        </div>
        <button onClick={onClose}
          style={{ width:'100%', padding:'0.65rem', background:'var(--gold)', border:'none', borderRadius:'var(--r-md)', color:'var(--black)', fontFamily:'Outfit', fontSize:'0.78rem', fontWeight:700, cursor:'pointer' }}>
          {tr('payment.view_ticket') || 'View ticket'} →
        </button>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding:'1.2rem 1.5rem 1.5rem', maxHeight:'88vh', overflowY:'auto' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.2rem' }}>
        <div>
          <div style={{ fontSize:'0.6rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.2rem' }}>{tr('payment.title') || 'Register'}</div>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', color:'var(--white)' }}>{event.title}</div>
          <div style={{ fontSize:'0.68rem', color:'var(--muted)', marginTop:'0.1rem' }}>{formatEventDate(event.date)} · {event.location}</div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1.2rem', padding:'0', lineHeight:1 }}>✕</button>
      </div>

      {/* Price summary */}
      <div style={{ background:'rgba(201,168,76,0.06)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-md)', padding:'0.8rem 1rem', marginBottom:'1.2rem' }}>
        {fullPrice > 0 && (
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.75rem', marginBottom: discount > 0 ? '0.3rem' : 0 }}>
            <span style={{ color:'var(--muted)' }}>{tr('payment.subtotal') || 'Subtotal'}</span>
            <span style={{ color:'var(--white)' }}>${fullPrice.toFixed(2)} CAD</span>
          </div>
        )}
        {discount > 0 && (
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.72rem', marginBottom:'0.3rem' }}>
            <span style={{ color:'#4EC994' }}>✦ {tr('payment.discount') || 'Member discount'}</span>
            <span style={{ color:'#4EC994' }}>−${discount.toFixed(2)}</span>
          </div>
        )}
        <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, fontSize:'0.88rem', paddingTop: discount > 0 ? '0.4rem' : 0, borderTop: discount > 0 ? '1px solid var(--gold-border)' : 'none', marginTop: discount > 0 ? '0.3rem' : 0 }}>
          <span style={{ color:'var(--gold)' }}>{tr('payment.total') || 'Total'}</span>
          <span style={{ color:'var(--gold)' }}>{isFree ? (tr('common.free') || 'Free') : `$${price.toFixed(2)} CAD`}</span>
        </div>
        {deadline && (
          <div style={{ fontSize:'0.6rem', color:'var(--muted)', marginTop:'0.4rem' }}>
            {tr('payment.refund_policy') || 'Refundable'} · before {deadline}
          </div>
        )}
      </div>

      {/* Contact */}
      <CardField label={tr('payment.name_on_card') || 'Full name'} error={errors.name}>
        <input value={name} onChange={e => setName(e.target.value)}
          style={{ width:'100%', padding:'0.55rem 0.75rem', background:'var(--dark)', border:`1px solid ${errors.name ? '#E07070' : 'var(--border)'}`, borderRadius:'var(--r-md)', color:'var(--white)', fontFamily:'Outfit', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }}
          placeholder="Alex Dupont"/>
      </CardField>

      <CardField label={tr('payment.email_label') || 'Email'} error={errors.email}>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          style={{ width:'100%', padding:'0.55rem 0.75rem', background:'var(--dark)', border:`1px solid ${errors.email ? '#E07070' : 'var(--border)'}`, borderRadius:'var(--r-md)', color:'var(--white)', fontFamily:'Outfit', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }}
          placeholder="alex@email.com"/>
      </CardField>

      {/* Card inputs (paid events only) */}
      {!isFree && (<>
        <CardField label={tr('payment.card_info') || 'Card number'} error={errors.card}>
          <div style={{ position:'relative' }}>
            <input value={card} onChange={e => setCard(formatCardNumber(e.target.value))}
              style={{ width:'100%', padding:'0.55rem 3rem 0.55rem 0.75rem', background:'var(--dark)', border:`1px solid ${errors.card ? '#E07070' : 'var(--border)'}`, borderRadius:'var(--r-md)', color:'var(--white)', fontFamily:'monospace', fontSize:'0.9rem', outline:'none', letterSpacing:'0.05em', boxSizing:'border-box' }}
              placeholder="1234 5678 9012 3456" maxLength={19}/>
            <div style={{ position:'absolute', right:'0.6rem', top:'50%', transform:'translateY(-50%)' }}>
              <CardIcon brand={brand}/>
            </div>
          </div>
        </CardField>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.7rem' }}>
          <CardField label="Expiry" error={errors.expiry}>
            <input value={expiry} onChange={e => setExpiry(formatExpiry(e.target.value))}
              style={{ width:'100%', padding:'0.55rem 0.75rem', background:'var(--dark)', border:`1px solid ${errors.expiry ? '#E07070' : 'var(--border)'}`, borderRadius:'var(--r-md)', color:'var(--white)', fontFamily:'monospace', fontSize:'0.9rem', outline:'none', letterSpacing:'0.05em', boxSizing:'border-box' }}
              placeholder="MM/YY" maxLength={5}/>
          </CardField>
          <CardField label="CVV" error={errors.cvc}>
            <input value={cvc} onChange={e => setCvc(formatCVC(e.target.value))}
              style={{ width:'100%', padding:'0.55rem 0.75rem', background:'var(--dark)', border:`1px solid ${errors.cvc ? '#E07070' : 'var(--border)'}`, borderRadius:'var(--r-md)', color:'var(--white)', fontFamily:'monospace', fontSize:'0.9rem', outline:'none', letterSpacing:'0.15em', boxSizing:'border-box' }}
              placeholder="•••" maxLength={4} type="password"/>
          </CardField>
        </div>

        {/* Dev test cards */}
        {isDev && (
          <div style={{ marginBottom:'0.8rem', padding:'0.5rem 0.7rem', background:'rgba(91,155,213,0.07)', border:'1px solid rgba(91,155,213,0.2)', borderRadius:'var(--r-sm)' }}>
            <div style={{ fontSize:'0.58rem', color:'rgba(91,155,213,0.8)', fontWeight:600, letterSpacing:'0.08em', marginBottom:'0.3rem' }}>DEV — TEST CARDS</div>
            <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
              {TEST_CARDS.map(tc => (
                <button key={tc.number} onClick={() => setCard(tc.number)}
                  style={{ fontSize:'0.6rem', padding:'0.15rem 0.45rem', background:'transparent', border:'1px solid rgba(91,155,213,0.3)', borderRadius:4, color:'rgba(91,155,213,0.9)', cursor:'pointer', fontFamily:'monospace' }}>
                  ···{tc.number.slice(-4)} {tc.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </>)}

      {/* Error */}
      {step === 'error' && errorMsg && (
        <div style={{ padding:'0.6rem 0.8rem', background:'rgba(224,112,112,0.1)', border:'1px solid rgba(224,112,112,0.3)', borderRadius:'var(--r-md)', fontSize:'0.75rem', color:'#E07070', marginBottom:'0.8rem' }}>
          ✗ {errorMsg}
        </div>
      )}

      {/* CTA */}
      <button onClick={step === 'processing' ? undefined : handlePay}
        disabled={step === 'processing'}
        style={{ width:'100%', padding:'0.85rem', background: step === 'processing' ? 'rgba(201,168,76,0.5)' : 'var(--gold)', border:'none', borderRadius:'var(--r-md)', color:'var(--black)', fontFamily:'Outfit', fontSize:'0.85rem', fontWeight:700, cursor: step === 'processing' ? 'default' : 'pointer', letterSpacing:'0.06em', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', transition:'all 0.2s', marginBottom:'0.4rem' }}>
        {step === 'processing' ? (
          <><span style={{ display:'inline-block', width:14, height:14, border:'2px solid rgba(0,0,0,0.3)', borderTopColor:'#0A0A0A', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/> {tr('payment.processing') || 'Processing…'}</>
        ) : isFree
          ? (tr('events.reserve_free') || 'Reserve my spot — Free')
          : `Confirm — $${price.toFixed(2)} CAD`
        }
      </button>

      {!isFree && (
        <div style={{ textAlign:'center', fontSize:'0.6rem', color:'var(--muted)', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem' }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          {tr('payment.secure') || 'Secured by Stripe'}
        </div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Refund modal ───────────────────────────────────────────────────────────
export function RefundModal({ event, receipt, onClose, onRefunded }) {
  const tr = useTr();
  const [step, setStep] = useState('confirm'); // confirm | processing | done
  const eligible = isRefundEligible(event, receipt);
  const price = receipt?.amount || 0;

  const handleRefund = async () => {
    if (!eligible || step === 'processing') return;
    setStep('processing');
    try {
      const { supabase: sb } = await import('../utils/supabase');
      const { data: { session } } = await sb.auth.getSession();
      await refundPayment({ paymentIntentId: receipt?.paymentId, amount: price }, session?.access_token);
      setStep('done');
      setTimeout(() => onRefunded?.(), 600);
    } catch {
      setStep('confirm'); // reset so user can retry
    }
  };

  return (
    <div style={{ padding:'1.5rem' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.2rem' }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', color:'var(--white)' }}>
          {tr('payment.cancel_title') || 'Cancel registration'}
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1.1rem' }}>✕</button>
      </div>

      <div style={{ padding:'0.8rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', marginBottom:'1rem' }}>
        <div style={{ fontSize:'0.88rem', color:'var(--white)', marginBottom:'0.2rem' }}>{event.title}</div>
        <div style={{ fontSize:'0.7rem', color:'var(--muted)' }}>{formatEventDate(event.date)} · {event.location}</div>
      </div>

      {step === 'done' ? (
        <div style={{ textAlign:'center', padding:'1rem 0' }}>
          <div style={{ marginBottom:'0.6rem', display:'flex', justifyContent:'center' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4EC994" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div style={{ fontSize:'0.82rem', color:'#4EC994' }}>{tr('payment.refunded') || 'Refund processed'}</div>
          {price > 0 && <div style={{ fontSize:'0.7rem', color:'var(--muted)', marginTop:'0.3rem' }}>${price.toFixed(2)} CAD will appear in 5–10 business days.</div>}
        </div>
      ) : (<>
        <div style={{ padding:'0.7rem 0.9rem', background: eligible ? 'rgba(78,201,148,0.07)' : 'rgba(224,112,112,0.07)', border:`1px solid ${eligible ? 'rgba(78,201,148,0.25)' : 'rgba(224,112,112,0.25)'}`, borderRadius:'var(--r-md)', marginBottom:'1rem' }}>
          <div style={{ fontSize:'0.75rem', color: eligible ? '#4EC994' : '#E07070', lineHeight:1.6 }}>
            {eligible ? (tr('payment.cancel_policy') || 'You are eligible for a full refund.') : (tr('payment.cancel_too_late') || 'The refund window has passed.')}
          </div>
        </div>
        <div style={{ display:'flex', gap:'0.5rem' }}>
          <button onClick={handleRefund} disabled={!eligible || step === 'processing'}
            style={{ flex:1, padding:'0.65rem', background: eligible ? 'rgba(224,112,112,0.15)' : 'rgba(255,255,255,0.05)', border:`1px solid ${eligible ? 'rgba(224,112,112,0.35)' : 'var(--border)'}`, borderRadius:'var(--r-md)', color: eligible ? '#E07070' : 'var(--muted)', fontFamily:'Outfit', fontSize:'0.78rem', fontWeight:600, cursor: eligible ? 'pointer' : 'default' }}>
            {step === 'processing' ? (tr('payment.processing') || 'Processing…') : (tr('payment.cancel_confirm') || 'Cancel & refund')}
          </button>
          <button onClick={onClose}
            style={{ padding:'0.65rem 1rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.75rem', cursor:'pointer' }}>
            Keep spot
          </button>
        </div>
      </>)}
    </div>
  );
}
